/**
 * Subscription Builder JavaScript - Mobile Optimized
 * FIXED: Removed incorrect notification when add-ons are already unlocked
 * FIXED: Hidden gift-related functionality
 * UPDATED: Added scent profiles for products
 */

// Scent profile mapping
const SCENT_PROFILES = {
  'Mud Men': 'Cedarwood & Patchouli',
  'Midnight Rush': 'Bergamot & Vetiver',
  'Freshly Minted': 'Spearmint & Basil',
  'Fresh Meadows': 'Lemongrass & Eucalyptus',
  'Clean Slate': 'Unscented',
  'Bergamot & Bloom': 'Bergamot, Patchouli & Jasmine',
  'Sage in the Woods': 'Sage, Fir & Cedarwood',
  'Espresso Italiano': 'Coffee, Vanilla & Cardamom'
};

class SubscriptionBuilder {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.data = window[`subscriptionBuilderData_${sectionId}`];
    
    // Add fallback data
    if (!this.data) {
      console.error('No data object found, using defaults');
      this.data = {
        subscriptionProducts: [],
        addonProducts: [],
        settings: {
          defaultQuantity: 4,
          currencySymbol: '$',
          moneyFormat: '${{amount}}'
        },
        plans: {
          annual: { discount: 0.15, interval: 365, billingCycle: 'annual', freeShipping: true },
          '6month': { discount: 0.10, interval: 180, billingCycle: 'bi-annual', freeShipping: false, freeShippingThreshold: 50 },
          quarterly: { discount: 0.05, interval: 90, billingCycle: 'quarterly', freeShipping: false, freeShippingThreshold: 70 }
        },
        quantities: {
          8: { basePrice: 80 },
          6: { basePrice: 60 },
          4: { basePrice: 40 }
        }
      };
    }
    
    this.selectedPlan = '6month'; // Default to 6-month plan
    this.selectedQuantity = 6; // Default to 6 bars (matching the selected card)
    this.subscriptionItems = {};
    this.addonItems = {};
    this.currentFilter = 'all';
    this.addonsUnlocked = false;
    
    this.init();
  }

  init() {
    if (!this.data || !this.data.subscriptionProducts) {
      console.error('Subscription Builder: No product data found');
      return;
    }
    
    this.renderProducts();
    this.bindEvents();
    this.updatePlanDisplay();
    this.updateQuantityDisplay();
    this.updateSummary();
    this.updateSteps();
    this.initMobileFeatures();
    this.checkAddonsUnlock();
    
    // Add mobile grid enforcement
    this.enforceMobileGrid();
    
    // Hide gift sections on init
    this.hideGiftElements();
  }

  hideGiftElements() {
    // Hide the gift box notice element
    const giftNotice = document.getElementById('gift-box-notice');
    if (giftNotice) {
      giftNotice.style.display = 'none';
      // Also remove from DOM to prevent any accidental display
      giftNotice.remove();
    }
    
    // Remove any gift-related messaging from plan benefits
    document.querySelectorAll('.benefit-item').forEach(item => {
      if (item.textContent.toLowerCase().includes('gift')) {
        item.style.display = 'none';
      }
    });
  }

  initMobileFeatures() {
    // Initialize collapsible summary on mobile
    if (window.innerWidth <= 781) {
      const summary = document.getElementById('subscription-summary');
      if (summary && !summary.classList.contains('collapsed')) {
        summary.classList.add('collapsed');
      }
    }
    
    // Initialize sticky bar
    this.initStickyBar();
    
    // Handle resize events
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  enforceMobileGrid() {
    if (window.innerWidth > 781) return;
    
    // Force grid styles for addon products with multiple attempts
    const enforceGrid = () => {
      const addonGrid = document.getElementById('addon-products');
      const addonContainer = document.querySelector('#addons-tab .products-container');
      const addonsTab = document.getElementById('addons-tab');
      
      // Remove any conflicting styles
      if (addonsTab) {
        addonsTab.style.width = '100%';
        addonsTab.style.padding = '0';
        addonsTab.style.margin = '0';
      }
      
      if (addonGrid) {
        // Force grid styles inline (highest specificity)
        addonGrid.style.cssText = `
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 0.75rem !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        `;
        
        // Ensure all product cards are properly styled
        const cards = addonGrid.querySelectorAll('.product-card');
        cards.forEach(card => {
          card.style.margin = '0';
          card.style.width = '100%';
          card.style.boxSizing = 'border-box';
        });
      }
      
      if (addonContainer && addonContainer !== addonGrid) {
        addonContainer.style.cssText = `
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 0.75rem !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        `;
      }
      
      // Also apply to subscription products for consistency
      const subGrid = document.getElementById('subscription-products');
      if (subGrid) {
        subGrid.style.cssText = `
          display: grid !important;
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 0.75rem !important;
          width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        `;
      }
    };
    
    // Run immediately and after delays to ensure it sticks
    enforceGrid();
    setTimeout(enforceGrid, 100);
    setTimeout(enforceGrid, 300);
    setTimeout(enforceGrid, 500);
  }

  initStickyBar() {
    // Show sticky bar on scroll
    let lastScrollY = 0;
    const stickyBar = document.getElementById('sticky-order-bar');
    const builderContent = document.querySelector('.builder-content');
    
    if (!stickyBar) return;
    
    // Initialize sticky bar with correct quantity on load
    const stickyItems = document.getElementById('sticky-items-count');
    if (stickyItems) {
      stickyItems.textContent = `0/${this.selectedQuantity} soaps`;
    }
    
    // Function to check if we should show the sticky bar
    const checkStickyBar = () => {
      const scrollY = window.scrollY;
      const heroSection = document.querySelector('.builder-hero');
      const heroBottom = heroSection ? heroSection.offsetTop + heroSection.offsetHeight : 200;
      
      // Show sticky bar when scrolled past hero and on mobile/tablet
      if (scrollY > heroBottom && window.innerWidth <= 1279) {
        stickyBar.style.display = 'block';
        setTimeout(() => {
          stickyBar.classList.add('visible');
          builderContent.classList.add('sticky-bar-visible');
        }, 10);
      } else {
        stickyBar.classList.remove('visible');
        builderContent.classList.remove('sticky-bar-visible');
        setTimeout(() => {
          if (!stickyBar.classList.contains('visible')) {
            stickyBar.style.display = 'none';
          }
        }, 350);
      }
      
      lastScrollY = scrollY;
    };
    
    // Check on scroll with throttling
    let scrollTimer;
    window.addEventListener('scroll', () => {
      if (scrollTimer) return;
      
      scrollTimer = setTimeout(() => {
        checkStickyBar();
        scrollTimer = null;
      }, 100);
    });
    
    // Initial check
    checkStickyBar();
  }

  handleResize() {
    const summary = document.getElementById('subscription-summary');
    if (window.innerWidth <= 781) {
      if (summary && !summary.querySelector('.summary-collapse-toggle').style.display) {
        summary.classList.add('collapsed');
      }
      this.enforceMobileGrid();
    } else {
      if (summary) {
        summary.classList.remove('collapsed');
      }
    }
  }

  renderProducts() {
    const subscriptionContainer = document.getElementById('subscription-products');
    const addonContainer = document.getElementById('addon-products');
    
    if (!subscriptionContainer || !addonContainer) {
      console.error('Product containers not found');
      return;
    }
    
    // Render subscription products
    if (this.data.subscriptionProducts.length > 0) {
      subscriptionContainer.innerHTML = this.data.subscriptionProducts
        .map(product => this.createProductCard(product, false))
        .join('');
    } else {
      subscriptionContainer.innerHTML = '<p class="empty-state">No subscription products available</p>';
    }
    
    // Render addon products with category data
    if (this.data.addonProducts && this.data.addonProducts.length > 0) {
      addonContainer.innerHTML = this.data.addonProducts
        .map(product => this.createProductCard(product, true))
        .join('');
    } else {
      addonContainer.innerHTML = '<p class="empty-state">No add-on products available</p>';
    }
    
    // Apply mobile grid after rendering with a delay
    setTimeout(() => this.enforceMobileGrid(), 50);
  }

  filterProducts(button) {
    const filter = button.dataset.filter;
    
    // Update active state
    document.querySelectorAll('.filter-button').forEach(btn => {
      btn.classList.remove('active');
    });
    button.classList.add('active');
    
    this.currentFilter = filter;
    const addonContainer = document.getElementById('addon-products');
    
    // Filter products and re-render to maintain grid
    const filteredProducts = this.data.addonProducts.filter(product => {
      return filter === 'all' || product.category === filter;
    });
    
    // Re-render filtered products
    if (filteredProducts.length > 0) {
      addonContainer.innerHTML = filteredProducts
        .map(product => this.createProductCard(product, true))
        .join('');
      
      // Restore quantities for previously selected items
      Object.entries(this.addonItems).forEach(([variantId, item]) => {
        const input = document.getElementById(`addon-qty-${variantId}`);
        if (input) {
          input.value = item.quantity;
          const card = input.closest('.product-card');
          if (card) {
            card.classList.add('selected');
          }
        }
      });
    } else {
      addonContainer.innerHTML = '<p class="no-products-message">No products in this category</p>';
    }
    
    // Force grid layout on mobile after filtering
    if (window.innerWidth <= 781) {
      addonContainer.style.display = 'grid';
      addonContainer.style.gridTemplateColumns = 'repeat(2, 1fr)';
      addonContainer.style.gap = '0.75rem';
      addonContainer.style.width = '100%';
    }
  }

  createProductCard(product, isAddon) {
    // Simple price display - no discounts
    const price = product.price;
    
    // Add category data attribute for filtering
    const categoryAttr = isAddon && product.category ? `data-category="${product.category}"` : '';
    
    // Get scent profile if available
    const scentProfile = SCENT_PROFILES[product.title] || '';
    const scentHTML = scentProfile ? `<div class="product-scent">${scentProfile}</div>` : '';
    
    return `
      <div class="product-card" 
           data-product-id="${product.id}" 
           data-variant-id="${product.variantId}" 
           data-is-addon="${isAddon}"
           ${categoryAttr}>
        <div class="product-image">
          ${product.image 
            ? `<img src="${product.image}" alt="${product.title}" loading="lazy">` 
            : '<span>No Image</span>'}
        </div>
        <div class="product-title">${product.title}</div>
        ${scentHTML}
        <div class="product-pricing">
          <div class="price-row">
            <span class="regular-price">${this.formatMoney(price)}</span>
          </div>
        </div>
        ${!product.available 
          ? '<div class="out-of-stock">Out of Stock</div>' 
          : ''}
        <div class="quantity-controls">
          <div class="quantity-input">
            <button 
              onclick="window.subscriptionBuilder.updateQuantity('${product.variantId}', -1, ${isAddon})" 
              ${!product.available ? 'disabled' : ''}
              aria-label="Decrease quantity">−</button>
            <input 
              type="number" 
              value="0" 
              min="0" 
              max="${isAddon ? '99' : this.selectedQuantity}" 
              id="${isAddon ? 'addon' : 'sub'}-qty-${product.variantId}" 
              aria-label="Quantity"
              readonly 
              ${!product.available ? 'disabled' : ''}>
            <button 
              onclick="window.subscriptionBuilder.updateQuantity('${product.variantId}', 1, ${isAddon})" 
              ${!product.available ? 'disabled' : ''}
              aria-label="Increase quantity">+</button>
          </div>
        </div>
      </div>
    `;
  }

  bindEvents() {
    // Plan selection
    document.querySelectorAll('.plan-card').forEach(card => {
      card.addEventListener('click', () => this.selectPlan(card));
    });

    // Quantity selection
    document.querySelectorAll('.quantity-card').forEach(card => {
      card.addEventListener('click', () => this.selectQuantity(card));
    });
    
    // Tab switching
    document.querySelectorAll('.tab-button').forEach(button => {
      button.addEventListener('click', () => this.switchTab(button));
    });

    // Filter buttons - add event listeners
    document.querySelectorAll('.filter-button').forEach(button => {
      button.addEventListener('click', () => this.filterProducts(button));
    });

    // Summary collapse toggle
    const collapseToggle = document.querySelector('.summary-collapse-toggle');
    if (collapseToggle) {
      collapseToggle.addEventListener('click', () => this.toggleSummaryCollapse());
    }

    // Checkout buttons
    const checkoutButton = document.getElementById('checkout-button');
    if (checkoutButton) {
      checkoutButton.addEventListener('click', () => this.proceedToCheckout());
    }

    const stickyCheckout = document.getElementById('sticky-checkout');
    if (stickyCheckout) {
      stickyCheckout.addEventListener('click', () => this.proceedToCheckout());
    }
  }

  selectPlan(card) {
    document.querySelectorAll('.plan-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    
    this.selectedPlan = card.dataset.plan;
    this.updatePlanDisplay();
    this.updateSummary();
    this.updateSteps();
  }

  selectQuantity(card) {
    const oldQuantity = this.selectedQuantity;
    const newQuantity = parseInt(card.dataset.quantity);
    const currentTotal = this.getTotalSubscriptionBars();
    
    // If reducing quantity and over the new limit, show adjustment modal
    if (currentTotal > newQuantity) {
      this.showQuantityAdjustmentModal(newQuantity, currentTotal, card);
      return; // Don't make the change yet
    }
    
    // Otherwise proceed with normal selection
    document.querySelectorAll('.quantity-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');
    
    this.selectedQuantity = newQuantity;
    
    // If increased quantity, just update button states
    if (newQuantity > oldQuantity && currentTotal > 0) {
      const available = this.selectedQuantity - currentTotal;
      this.showMessage(`You can now select ${available} more bar${available > 1 ? 's' : ''}`, 'success');
    }
    
    this.updateQuantityDisplay();
    this.updateSummary();
    this.updateSteps();
    this.checkAddonsUnlock();
    this.updateButtonStates();
  }
  
  showQuantityAdjustmentModal(newQuantity, currentTotal, card) {
    const excess = currentTotal - newQuantity;
    
    // If already at or below target, just apply the change
    if (excess <= 0) {
      document.querySelectorAll('.quantity-card').forEach(c => c.classList.remove('selected'));
      card.classList.add('selected');
      this.selectedQuantity = newQuantity;
      this.updateQuantityDisplay();
      this.updateSummary();
      this.updateSteps();
      this.checkAddonsUnlock();
      this.updateButtonStates();
      return;
    }
    
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'quantity-adjustment-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      padding: 2rem;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      max-height: 70vh;
      overflow-y: auto;
      box-shadow: 0 10px 40px rgba(0,0,0,0.2);
    `;
    
    modalContent.innerHTML = `
      <h3 style="margin-top: 0; color: var(--color-blue-200); font-family: var(--font-garamond); font-size: 1.5rem;">
        Adjust Your Selection
      </h3>
      <p style="color: var(--color-gray-350); margin-bottom: 1.5rem;">
        You have ${currentTotal} bars selected but are changing to ${newQuantity} bars per delivery. 
        Please remove ${excess} bar${excess > 1 ? 's' : ''} to continue.
      </p>
      <div id="modal-product-list" style="margin-bottom: 1.5rem;">
        ${this.renderModalProductList()}
      </div>
      <div style="text-align: center;">
        <button id="modal-cancel" style="
          padding: 0.75rem 2rem;
          background: white;
          border: 1px solid var(--color-gray-150);
          color: var(--color-gray-350);
          cursor: pointer;
          border-radius: 4px;
          font-family: var(--font-grotesk);
        ">Cancel</button>
      </div>
    `;
    
    modal.appendChild(modalContent);
    document.body.appendChild(modal);
    
    // Store modal state
    this.modalTargetQuantity = newQuantity;
    this.modalCard = card;
    this.modalElement = modal;
    
    // Bind modal events
    this.bindModalEvents(modal);
  }
  
  renderModalProductList() {
    let html = '<div style="border: 1px solid var(--color-gray-150); border-radius: 4px; padding: 1rem;">';
    
    Object.entries(this.subscriptionItems).forEach(([variantId, item]) => {
      html += `
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid var(--color-gray-100);">
          <span style="color: var(--color-blue-200);">${item.product.title}</span>
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <button class="modal-minus" data-variant="${variantId}" style="
              width: 24px;
              height: 24px;
              border: 1px solid var(--color-gray-150);
              background: white;
              cursor: pointer;
              border-radius: 4px;
              color: var(--color-blue-200);
            ">−</button>
            <span class="modal-qty" data-variant="${variantId}" style="min-width: 20px; text-align: center;">
              ${item.quantity}
            </span>
            <button class="modal-plus" data-variant="${variantId}" style="
              width: 24px;
              height: 24px;
              border: 1px solid var(--color-gray-150);
              background: white;
              cursor: pointer;
              border-radius: 4px;
              color: var(--color-blue-200);
            " disabled>+</button>
          </div>
        </div>
      `;
    });
    
    html += '</div>';
    return html;
  }
  
  bindModalEvents(modal) {
    // Cancel button
    modal.querySelector('#modal-cancel').addEventListener('click', () => {
      document.body.removeChild(modal);
    });
    
    // Quantity buttons in modal
    modal.querySelectorAll('.modal-minus, .modal-plus').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const variantId = e.target.dataset.variant;
        const isPlus = e.target.classList.contains('modal-plus');
        this.updateModalQuantity(variantId, isPlus ? 1 : -1, modal);
      });
    });
    
    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  }
  
  updateModalQuantity(variantId, change, modal) {
    const item = this.subscriptionItems[variantId];
    if (!item) return;
    
    const newQty = Math.max(0, item.quantity + change);
    
    // Update the item or remove it
    if (newQty === 0) {
      delete this.subscriptionItems[variantId];
    } else {
      item.quantity = newQty;
    }
    
    // Update modal display
    const qtySpan = modal.querySelector(`.modal-qty[data-variant="${variantId}"]`);
    if (qtySpan) qtySpan.textContent = newQty;
    
    // Check current total
    const currentTotal = this.getTotalSubscriptionBars();
    console.log('Current total:', currentTotal, 'Target:', this.modalTargetQuantity);
    
    // Auto-close when we reach the target
    if (currentTotal === this.modalTargetQuantity || currentTotal < this.modalTargetQuantity) {
      console.log('Target reached! Closing modal...');
      
      // Apply the changes immediately
      document.querySelectorAll('.quantity-card').forEach(c => c.classList.remove('selected'));
      this.modalCard.classList.add('selected');
      this.selectedQuantity = this.modalTargetQuantity;
      
      // Close modal first to avoid any conflicts
      try {
        if (modal && modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
      } catch (e) {
        console.error('Error removing modal:', e);
      }
      
      // Then update everything else
      setTimeout(() => {
        this.refreshProductDisplay();
        this.updateQuantityDisplay();
        this.updateSummary();
        this.updateSteps();
        this.checkAddonsUnlock();
        this.updateButtonStates();
        this.showMessage(`Selection adjusted to ${this.modalTargetQuantity} bars`, 'success');
      }, 100);
      
      return;
    }
    
    // Still need to remove more - update button states
    modal.querySelectorAll('.modal-minus').forEach(btn => {
      const btnVariantId = btn.dataset.variant;
      const btnItem = this.subscriptionItems[btnVariantId];
      const qty = btnItem ? btnItem.quantity : 0;
      btn.disabled = qty === 0;
    });
  }
  
  refreshProductDisplay() {
    // Update all product card displays to match internal state
    document.querySelectorAll('.product-card:not([data-is-addon="true"])').forEach(card => {
      const variantId = card.dataset.variantId;
      const input = card.querySelector(`#sub-qty-${variantId}`);
      
      if (input) {
        const item = this.subscriptionItems[variantId];
        input.value = item ? item.quantity : 0;
        
        if (item && item.quantity > 0) {
          card.classList.add('selected');
        } else {
          card.classList.remove('selected');
        }
      }
    });
    
    this.updateButtonStates();
  }
  
  updateButtonStates() {
    const currentTotal = this.getTotalSubscriptionBars();
    const canAddMore = currentTotal < this.selectedQuantity;
    
    // Update all subscription product buttons
    document.querySelectorAll('.product-card:not([data-is-addon="true"])').forEach(card => {
      const input = card.querySelector('input[type="number"]');
      const plusBtn = card.querySelector('.quantity-input button:last-child');
      const minusBtn = card.querySelector('.quantity-input button:first-child');
      
      if (input && plusBtn) {
        const currentQty = parseInt(input.value) || 0;
        
        // Update max attribute to new quantity
        input.max = this.selectedQuantity;
        
        // Plus button logic
        if (currentTotal >= this.selectedQuantity) {
          plusBtn.disabled = true;
        } else {
          // Check if adding to this specific item would exceed limit
          const othersTotal = currentTotal - currentQty;
          plusBtn.disabled = (othersTotal + currentQty + 1) > this.selectedQuantity;
        }
        
        // Minus button always enabled if quantity > 0
        if (minusBtn) {
          minusBtn.disabled = currentQty === 0;
        }
      }
    });
  }

  updatePlanDisplay() {
    const planDisplay = document.getElementById('selected-plan-display');
    const deliveryNote = document.getElementById('delivery-note');
    
    const planNames = {
      'annual': 'Annual Prepaid (4 deliveries)',
      '6month': '6-Month Prepaid (2 deliveries)',
      'quarterly': 'Quarterly Subscription'
    };
    
    if (planDisplay) {
      planDisplay.textContent = planNames[this.selectedPlan];
    }
    
    // NO GIFT NOTICE DISPLAY - removed this section entirely
    
    if (deliveryNote && this.data.plans && this.data.plans[this.selectedPlan]) {
      if (this.data.plans[this.selectedPlan].freeShipping) {
        deliveryNote.textContent = 'Free shipping included on all deliveries';
      } else {
        const threshold = this.data.plans[this.selectedPlan].freeShippingThreshold;
        if (threshold) {
          deliveryNote.textContent = `Free shipping on orders over ${threshold}`;
        } else {
          deliveryNote.textContent = 'Shipping calculated at checkout';
        }
      }
      
      // Add billing clarification
      if (this.selectedPlan === 'quarterly') {
        deliveryNote.textContent += ' • Billed every 3 months';
      } else if (this.selectedPlan === '6month') {
        deliveryNote.textContent += ' • One payment covers 2 deliveries';
      } else if (this.selectedPlan === 'annual') {
        deliveryNote.textContent += ' • One payment covers 4 deliveries';
      }
    }
  }

  updateQuantityDisplay() {
    const quantityDisplay = document.getElementById('selected-quantity-display');
    const barsNeeded = document.getElementById('bars-needed');
    const totalBarsNeeded = document.getElementById('total-bars-needed');
    const stickyItems = document.getElementById('sticky-items-count');
    
    if (quantityDisplay) {
      quantityDisplay.textContent = `${this.selectedQuantity} bars/delivery`;
    }
    
    if (barsNeeded) {
      barsNeeded.textContent = this.selectedQuantity;
    }
    
    if (totalBarsNeeded) {
      totalBarsNeeded.textContent = this.selectedQuantity;
    }
    
    // Update sticky bar to show correct quantity
    if (stickyItems) {
      const currentTotal = this.getTotalSubscriptionBars();
      const addonCount = Object.keys(this.addonItems).length;
      if (addonCount > 0) {
        stickyItems.textContent = `${currentTotal}/${this.selectedQuantity} soaps + ${addonCount} addon${addonCount > 1 ? 's' : ''}`;
      } else {
        stickyItems.textContent = `${currentTotal}/${this.selectedQuantity} soaps`;
      }
    }
    
    // Don't auto-clear selections anymore - just update button states
    const currentTotal = this.getTotalSubscriptionBars();
    if (currentTotal > this.selectedQuantity) {
      // Keep selections but update UI to show over-limit state
      document.getElementById('checkout-button').disabled = true;
      document.getElementById('sticky-checkout').disabled = true;
    }
  }

  switchTab(button) {
    // If button is disabled, it shouldn't even fire, but just in case
    if (button.disabled) {
      return;
    }
    
    // Simply switch tabs - no messages needed since button is only enabled when allowed
    document.querySelectorAll('.tab-button').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
    
    button.classList.add('active');
    document.getElementById(button.dataset.tab + '-tab').classList.add('active');
    
    // Apply mobile grid when switching to addons
    if (button.dataset.tab === 'addons') {
      this.enforceMobileGrid();
    }
  }

  toggleSummaryCollapse() {
    const summary = document.getElementById('subscription-summary');
    if (summary) {
      summary.classList.toggle('collapsed');
      const toggle = summary.querySelector('.summary-collapse-toggle');
      if (toggle) {
        const isExpanded = !summary.classList.contains('collapsed');
        toggle.setAttribute('aria-expanded', isExpanded);
      }
    }
  }

  checkAddonsUnlock() {
    const totalBars = this.getTotalSubscriptionBars();
    const addonsTabButton = document.getElementById('addons-tab-button');
    const addonBadge = document.getElementById('addon-badge');
    const step4 = document.getElementById('step-4');
    
    if (totalBars === this.selectedQuantity) {
      // Unlock add-ons
      if (!this.addonsUnlocked) {
        this.addonsUnlocked = true;
        if (addonsTabButton) {
          addonsTabButton.disabled = false;
          if (addonBadge) {
            addonBadge.style.display = 'inline-block';
          }
        }
        if (step4) {
          step4.classList.add('active');
        }
        
        // Show success notification only once when unlocking
        this.showMessage('Add-ons unlocked! You can now add extra products', 'success');
      }
    } else {
      // Lock add-ons - no error message here, just lock the functionality
      this.addonsUnlocked = false;
      if (addonsTabButton) {
        addonsTabButton.disabled = true;
        if (addonBadge) {
          addonBadge.style.display = 'none';
        }
      }
      if (step4) {
        step4.classList.remove('active');
      }
    }
  }

  updateQuantity(variantId, change, isAddon) {
    const inputId = (isAddon ? 'addon' : 'sub') + '-qty-' + variantId;
    const input = document.getElementById(inputId);
    
    if (!input) return;
    
    let currentValue = parseInt(input.value);
    let newValue = currentValue + change;
    
    // For subscription items, enforce the selected quantity limit
    if (!isAddon) {
      const currentTotal = this.getTotalSubscriptionBars();
      const otherBarsCount = currentTotal - currentValue;
      const maxAllowed = this.selectedQuantity - otherBarsCount;
      
      // Don't allow going over selected quantity total when adding
      if (change > 0 && currentTotal >= this.selectedQuantity) {
        return;
      }
      
      newValue = Math.min(newValue, maxAllowed);
    }
    
    newValue = Math.max(0, newValue);
    input.value = newValue;
    
    // Update stored items
    const card = input.closest('.product-card');
    if (card) {
      if (newValue > 0) {
        card.classList.add('selected');
        const items = isAddon ? this.addonItems : this.subscriptionItems;
        items[variantId] = {
          quantity: newValue,
          product: this.findProduct(variantId, isAddon)
        };
      } else {
        card.classList.remove('selected');
        if (isAddon) {
          delete this.addonItems[variantId];
        } else {
          delete this.subscriptionItems[variantId];
        }
      }
    }
    
    // Update plus/minus button states for all products
    if (!isAddon) {
      const newTotal = this.getTotalSubscriptionBars();
      document.querySelectorAll('.product-card:not([data-is-addon="true"])').forEach(productCard => {
        const productInput = productCard.querySelector('input[type="number"]');
        const plusBtn = productCard.querySelector('.quantity-input button:last-child');
        const minusBtn = productCard.querySelector('.quantity-input button:first-child');
        
        if (productInput && plusBtn && minusBtn) {
          const qty = parseInt(productInput.value) || 0;
          
          // Plus button disabled only if we're at max AND this would go over
          plusBtn.disabled = newTotal >= this.selectedQuantity && qty === parseInt(productInput.value);
          
          // Minus button ALWAYS enabled if quantity > 0
          minusBtn.disabled = qty === 0;
        }
      });
    }
    
    this.updateSummary();
    this.updateSteps();
    this.checkAddonsUnlock();
  }

  findProduct(variantId, isAddon) {
    const products = isAddon ? this.data.addonProducts : this.data.subscriptionProducts;
    return products.find(p => p.variantId == variantId);
  }

  getTotalSubscriptionBars() {
    return Object.values(this.subscriptionItems).reduce((sum, item) => sum + item.quantity, 0);
  }

  updateSteps() {
    const steps = document.querySelectorAll('.step');
    steps.forEach(s => s.classList.remove('active'));
    
    const step1 = document.getElementById('step-1');
    const step2 = document.getElementById('step-2');
    const step3 = document.getElementById('step-3');
    
    if (step1) step1.classList.add('active');
    
    if (this.selectedPlan && step2) {
      step2.classList.add('active');
    }
    
    if (this.selectedQuantity && step3) {
      step3.classList.add('active');
    }
    
    // Step 4 is handled in checkAddonsUnlock()
  }

  togglePickupLocation() {
    const deliverySelect = document.getElementById('delivery-preference');
    const pickupSection = document.getElementById('pickup-location-section');
    
    if (deliverySelect.value === 'pickup') {
      pickupSection.style.display = 'block';
    } else {
      pickupSection.style.display = 'none';
      // Clear pickup location if switching back to shipping
      const pickupLocation = document.getElementById('pickup-location');
      if (pickupLocation) pickupLocation.value = '';
    }
  }

  updateSummary() {
    const selectedCount = document.getElementById('selected-count');
    const selectedSoapsList = document.getElementById('selected-soaps-list');
    const selectedAddonsList = document.getElementById('selected-addons-list');
    const addonsSection = document.getElementById('addons-section');
    const totalAmount = document.getElementById('total-amount');
    const checkoutButton = document.getElementById('checkout-button');
    const summaryPreview = document.getElementById('summary-preview');
    const totalLabel = document.getElementById('total-label');
    
    // Sticky bar elements
    const stickyItems = document.getElementById('sticky-items-count');
    const stickyTotal = document.getElementById('sticky-total');
    const stickyCheckout = document.getElementById('sticky-checkout');
    
    const totalBars = this.getTotalSubscriptionBars();
    if (selectedCount) selectedCount.textContent = totalBars;
    
    // Update sticky bar items count
    if (stickyItems) {
      const addonCount = Object.keys(this.addonItems).length;
      if (addonCount > 0) {
        stickyItems.textContent = `${totalBars}/${this.selectedQuantity} soaps + ${addonCount} addon${addonCount > 1 ? 's' : ''}`;
      } else {
        stickyItems.textContent = `${totalBars}/${this.selectedQuantity} soaps`;
      }
    }
    
    // Update selected soaps list
    if (selectedSoapsList) {
      if (Object.keys(this.subscriptionItems).length > 0) {
        selectedSoapsList.innerHTML = Object.entries(this.subscriptionItems)
          .map(([variantId, item]) => `
            <div class="summary-item">
              <span>${item.product.title}</span>
              <span class="summary-item-quantity">×${item.quantity}</span>
            </div>
          `).join('');
      } else {
        selectedSoapsList.innerHTML = '<p class="empty-state">No soaps selected yet</p>';
      }
    }
    
    // Update add-ons list
    if (addonsSection && selectedAddonsList) {
      if (Object.keys(this.addonItems).length > 0) {
        addonsSection.style.display = 'block';
        selectedAddonsList.innerHTML = Object.entries(this.addonItems)
          .map(([variantId, item]) => `
            <div class="summary-item">
              <span>${item.product.title}</span>
              <span class="summary-item-quantity">×${item.quantity}</span>
            </div>
          `).join('');
      } else {
        addonsSection.style.display = 'none';
      }
    }
    
    // Calculate totals
    let subscriptionTotal = 0;
    let addonTotal = 0;
    
    // Calculate subscription items total
    Object.values(this.subscriptionItems).forEach(item => {
      subscriptionTotal += item.product.price * item.quantity;
    });
    
    // Calculate add-on items total
    Object.values(this.addonItems).forEach(item => {
      addonTotal += item.product.price * item.quantity;
    });
    
    // Update total label based on plan
    if (totalLabel) {
      const billingLabels = {
        'annual': 'Annual Prepaid Total (4 deliveries)',
        '6month': '6-Month Prepaid Total (2 deliveries)',
        'quarterly': 'Quarterly Total (per delivery, billed recurring)'
      };
      totalLabel.textContent = billingLabels[this.selectedPlan] || 'Per Delivery';
    }
    
    // Apply multiplier based on plan
    let total = 0;
    if (this.selectedPlan === 'annual') {
      total = (subscriptionTotal * 4) + addonTotal;
    } else if (this.selectedPlan === '6month') {
      total = (subscriptionTotal * 2) + addonTotal;
    } else {
      total = subscriptionTotal + addonTotal;
    }
    
    // Update all total displays
    const formattedTotal = this.formatMoney(total);
    if (totalAmount) totalAmount.textContent = formattedTotal;
    if (summaryPreview) summaryPreview.textContent = formattedTotal;
    if (stickyTotal) stickyTotal.textContent = formattedTotal;
    
    // Update checkout buttons
    if (totalBars === this.selectedQuantity) {
      const buttonText = this.selectedPlan === 'quarterly' 
        ? 'Start Recurring Subscription' 
        : 'Complete Prepaid Order';
      const stickyButtonText = this.selectedPlan === 'quarterly'
        ? 'Start Subscription'
        : 'Complete Order';
      
      if (checkoutButton) {
        checkoutButton.disabled = false;
        checkoutButton.textContent = buttonText;
      }
      
      if (stickyCheckout) {
        stickyCheckout.disabled = false;
        stickyCheckout.textContent = stickyButtonText;
      }
    } else if (totalBars > this.selectedQuantity) {
      const excess = totalBars - this.selectedQuantity;
      const buttonText = `Remove ${excess} Bar${excess > 1 ? 's' : ''} to Continue`;
      
      if (checkoutButton) {
        checkoutButton.disabled = true;
        checkoutButton.textContent = buttonText;
      }
      
      if (stickyCheckout) {
        stickyCheckout.disabled = true;
        stickyCheckout.textContent = buttonText;
      }
    } else {
      const remaining = this.selectedQuantity - totalBars;
      const buttonText = `Select ${remaining} More Soap${remaining > 1 ? 's' : ''}`;
      
      if (checkoutButton) {
        checkoutButton.disabled = true;
        checkoutButton.textContent = buttonText;
      }
      
      if (stickyCheckout) {
        stickyCheckout.disabled = true;
        stickyCheckout.textContent = buttonText;
      }
    }
  }

async proceedToCheckout() {
    const checkoutButton = document.getElementById('checkout-button');
    const stickyCheckout = document.getElementById('sticky-checkout');
    
    if (checkoutButton) {
      checkoutButton.disabled = true;
      checkoutButton.textContent = 'Processing...';
    }
    if (stickyCheckout) {
      stickyCheckout.disabled = true;
      stickyCheckout.textContent = 'Processing...';
    }
    
    try {
      const cartItems = [];
      const deliveryPreference = document.getElementById('delivery-preference');
      const pickupLocation = document.getElementById('pickup-location');
      
      if (deliveryPreference && deliveryPreference.value === 'pickup') {
        if (!pickupLocation || !pickupLocation.value) {
          this.showMessage('Please select a pickup location', 'error');
          this.resetCheckoutButtons();
          return;
        }
      }
      
      const deliveryMultiplier = {
        'annual': 4,
        '6month': 2,
        'quarterly': 1
      }[this.selectedPlan];
      
      const planDisplayNames = {
        'annual': 'Annual Prepay (One Time)',
        '6month': '6 Month Prepay (One Time)',
        'quarterly': 'Quarterly Recurring'
      };
      
      const totalBars = this.selectedQuantity * deliveryMultiplier;
      const barsPerShipmentText = `${this.selectedQuantity} Bars/Quarter${deliveryMultiplier > 1 ? ` (${totalBars} Bars Total)` : ''}`;
      
      // Build subscription items
      for (const [variantId, item] of Object.entries(this.subscriptionItems)) {
        const properties = {
          'Plan': planDisplayNames[this.selectedPlan],
          'Bars Per Shipment': barsPerShipmentText,
          'Shipping Frequency': 'Ships Every ~90 Days'
        };
        
        if (deliveryPreference && deliveryPreference.value === 'pickup' && pickupLocation && pickupLocation.value) {
          properties['Delivery Method'] = 'Local Pickup';
          properties['Pickup Location'] = pickupLocation.options[pickupLocation.selectedIndex].text;
        }
        
        cartItems.push({
          id: parseInt(variantId),
          quantity: item.quantity * deliveryMultiplier,
          properties: properties
        });
      }
      
      // Build addon items
      Object.entries(this.addonItems).forEach(([variantId, item]) => {
        cartItems.push({
          id: parseInt(variantId),
          quantity: item.quantity,
          properties: {
            'Type': 'One-Time Add-On'
          }
        });
      });
      
      // NO GIFT PRODUCT ADDED - removed gift logic entirely
      
      console.log('Adding items to cart:', cartItems);
      
      // Add items to cart and wait for response
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: cartItems })
      });
      
      // Parse the response
      const data = await response.json();
      console.log('Cart API response:', data);
      
      // Wait a moment for cart to update, then redirect
      setTimeout(() => {
        window.location.href = '/cart';
      }, 300);
      
    } catch (error) {
      console.error('Checkout error:', error);
      // Show a generic error but still try to redirect
      // In case items were added despite the error
      this.showMessage('Processing order...', 'info');
      setTimeout(() => {
        window.location.href = '/cart';
      }, 500);
    }
  }

  resetCheckoutButtons() {
    const checkoutButton = document.getElementById('checkout-button');
    const stickyCheckout = document.getElementById('sticky-checkout');
    
    const buttonText = this.selectedPlan === 'quarterly' 
      ? 'Try Again - Start Subscription' 
      : 'Try Again - Complete Order';
    
    if (checkoutButton) {
      checkoutButton.disabled = false;
      checkoutButton.textContent = buttonText;
    }
    
    if (stickyCheckout) {
      stickyCheckout.disabled = false;
      stickyCheckout.textContent = 'Try Again';
    }
  }

  formatMoney(amount) {
    if (this.data.settings && this.data.settings.moneyFormat) {
      return this.data.settings.moneyFormat
        .replace('{{amount}}', amount.toFixed(2))
        .replace('{{amount_no_decimals}}', Math.round(amount).toString());
    }
    return `${amount.toFixed(2)}`;
  }

  showMessage(message, type = 'info') {
    const messageEl = document.createElement('div');
    messageEl.className = `subscription-message subscription-message--${type}`;
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#ff4444' : type === 'success' ? '#4a7c59' : '#333'};
      color: white;
      padding: 15px 20px;
      border-radius: 4px;
      z-index: 9999;
      max-width: 300px;
      animation: slideIn 0.3s ease;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    `;
    
    document.body.appendChild(messageEl);
    
    setTimeout(() => {
      messageEl.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => messageEl.remove(), 300);
    }, 3000);
  }

  filterProductsByCategory(category) {
    this.currentFilter = category;
    const addonContainer = document.getElementById('addon-products');
    
    const filteredProducts = this.data.addonProducts.filter(product => {
      return category === 'all' || product.category === category;
    });
    
    if (filteredProducts.length > 0) {
      addonContainer.innerHTML = filteredProducts
        .map(product => this.createProductCard(product, true))
        .join('');
      
      Object.entries(this.addonItems).forEach(([variantId, item]) => {
        const input = document.getElementById(`addon-qty-${variantId}`);
        if (input) {
          input.value = item.quantity;
          const card = input.closest('.product-card');
          if (card) {
            card.classList.add('selected');
          }
        }
      });
    } else {
      addonContainer.innerHTML = '<p class="no-products-message">No products in this category</p>';
    }
    
    this.enforceMobileGrid();
  }
}

// Function to initialize the builder
function initializeSubscriptionBuilder() {
  const section = document.querySelector('.subscription-builder');
  
  if (section) {
    const fullId = section.id;
    const sectionId = fullId.replace('subscription-builder-', '').replace(/-/g, '_');
    const dataKey = `subscriptionBuilderData_${sectionId}`;
    
    console.log('Initializing with section ID:', sectionId);
    console.log('Looking for data key:', dataKey);
    
    // Wait for data to be available
    let attempts = 0;
    const maxAttempts = 20;
    
    const tryInitialize = () => {
      attempts++;
      
      if (window[dataKey]) {
        console.log('Data found, initializing builder');
        window.subscriptionBuilder = new SubscriptionBuilder(sectionId);
        return true;
      } else if (attempts < maxAttempts) {
        console.log(`Waiting for data... attempt ${attempts}/${maxAttempts}`);
        setTimeout(tryInitialize, 100);
        return false;
      } else {
        console.error('No data found after waiting, giving up');
        return false;
      }
    };
    
    tryInitialize();
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', initializeSubscriptionBuilder);

// Handle Theme Editor events
if (window.Shopify && window.Shopify.designMode) {
  console.log('Theme Editor mode detected');
  
  // Listen for section events
  document.addEventListener('shopify:section:load', function(event) {
    console.log('Section loaded:', event.detail);
    // Wait a bit for the DOM and data to settle
    setTimeout(initializeSubscriptionBuilder, 200);
  });
  
  document.addEventListener('shopify:section:select', function(event) {
    console.log('Section selected:', event.detail);
    // Reinitialize to ensure everything is connected
    setTimeout(initializeSubscriptionBuilder, 100);
  });
}

// Also try to initialize if the script loads after DOM ready
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(initializeSubscriptionBuilder, 100);
}

// Add animation styles
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

SubscriptionBuilder.prototype.selectMobilePlan = function(planId) {
  // Find and click the actual plan card (which triggers existing functionality)
  const actualCard = document.querySelector(`.plan-card[data-plan="${planId}"]`);
  if (actualCard) {
    this.selectPlan(actualCard);
  }
  
  // Update mobile UI tabs
  document.querySelectorAll('.mobile-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.plan === planId);
  });
  
  // Show corresponding plan content
  document.querySelectorAll('.mobile-plan-content').forEach(content => {
    if (content.dataset.plan === planId) {
      content.style.display = 'block';
      content.classList.add('active');
    } else {
      content.style.display = 'none';
      content.classList.remove('active');
    }
  });
  
  // Update pricing in the plan content
  this.updateMobilePlanPricing();
};

SubscriptionBuilder.prototype.selectMobileQuantity = function(quantity) {
  // Find and click the actual quantity card (which triggers existing functionality)
  const actualCard = document.querySelector(`.quantity-card[data-quantity="${quantity}"]`);
  if (actualCard) {
    this.selectQuantity(actualCard);
  }
  
  // Update mobile UI
  document.querySelectorAll('.mobile-qty-btn').forEach(btn => {
    btn.classList.toggle('active', parseInt(btn.dataset.qty) === quantity);
  });
  
  // Update pricing and selection text in the active plan content
  this.updateMobilePlanPricing();
};

SubscriptionBuilder.prototype.updateMobilePlanPricing = function() {
  const basePrice = this.selectedQuantity * 10; // $10 per bar
  
  // Define usage types based on quantity
  const usageTypes = {
    4: 'Light Use',
    6: 'Regular Use',
    8: 'Family/Heavy Use'
  };
  
  const currentUsageType = usageTypes[this.selectedQuantity] || 'Regular Use';
  
  // Update Annual Plan
  const annualPrice = document.getElementById('annual-mobile-price');
  const annualMonthly = document.getElementById('annual-mobile-monthly');
  const annualSelection = document.getElementById('annual-selection');
  
  if (annualPrice) {
    annualPrice.textContent = `$${basePrice * 4}`;
  }
  if (annualMonthly) {
    const monthlyAmount = Math.round((basePrice * 4) / 12);
    annualMonthly.textContent = `(~$${monthlyAmount}/mo)`;
  }
  if (annualSelection) {
    annualSelection.innerHTML = `• <span class="selection-quantity">${this.selectedQuantity}</span> bars per 90-day shipment <span class="usage-type">(${currentUsageType})</span>`;
  }
  
  // Update 6-Month Plan
  const sixMonthPrice = document.getElementById('6month-mobile-price');
  const sixMonthMonthly = document.getElementById('6month-mobile-monthly');
  const sixMonthSelection = document.getElementById('6month-selection');
  
  if (sixMonthPrice) {
    sixMonthPrice.textContent = `$${basePrice * 2}`;
  }
  if (sixMonthMonthly) {
    const monthlyAmount = Math.round((basePrice * 2) / 6);
    sixMonthMonthly.textContent = `(~$${monthlyAmount}/mo)`;
  }
  if (sixMonthSelection) {
    sixMonthSelection.innerHTML = `• <span class="selection-quantity">${this.selectedQuantity}</span> bars per 90-day shipment <span class="usage-type">(${currentUsageType})</span>`;
  }
  
  // Update Quarterly Plan
  const quarterlyPrice = document.getElementById('quarterly-mobile-price');
  const quarterlyMonthly = document.getElementById('quarterly-mobile-monthly');
  const quarterlySelection = document.getElementById('quarterly-selection');
  
  if (quarterlyPrice) {
    quarterlyPrice.textContent = `$${basePrice}`;
  }
  if (quarterlyMonthly) {
    const monthlyAmount = Math.round(basePrice / 3);
    quarterlyMonthly.textContent = `(~$${monthlyAmount}/mo)`;
  }
  if (quarterlySelection) {
    quarterlySelection.innerHTML = `• <span class="selection-quantity">${this.selectedQuantity}</span> bars per 90-day shipment <span class="usage-type">(${currentUsageType})</span>`;
  }
};

// Initialize mobile view on load if needed
SubscriptionBuilder.prototype.initMobileView = function() {
  if (window.innerWidth <= 767) {
    // Make sure mobile wrapper is visible
    const mobileWrapper = document.querySelector('.mobile-plan-quantity-wrapper');
    if (mobileWrapper) {
      mobileWrapper.style.display = 'block';
    }
    
    // Hide desktop sections
    const desktopSections = document.querySelectorAll('.desktop-tablet-only');
    desktopSections.forEach(section => {
      section.style.display = 'none';
    });
    
    // Set initial mobile state to match selected values
    this.updateMobilePlanPricing();
    
    // Make sure correct plan is showing
    const activePlan = this.selectedPlan || 'annual';
    this.selectMobilePlan(activePlan);
    
    // Make sure correct quantity is selected
    const activeQuantity = this.selectedQuantity || 6;
    document.querySelectorAll('.mobile-qty-btn').forEach(btn => {
      btn.classList.toggle('active', parseInt(btn.dataset.qty) === activeQuantity);
    });
  }
};

// Update the existing init method if not already done
const originalInit = SubscriptionBuilder.prototype.init;
SubscriptionBuilder.prototype.init = function() {
  // Call original init
  originalInit.call(this);
  
  // Initialize mobile view
  this.initMobileView();
  
  // Handle window resize
  window.addEventListener('resize', () => {
    if (window.innerWidth <= 767) {
      this.initMobileView();
    } else {
      // Show desktop sections
      const desktopSections = document.querySelectorAll('.desktop-tablet-only');
      desktopSections.forEach(section => {
        section.style.display = '';
      });
      
      // Hide mobile wrapper
      const mobileWrapper = document.querySelector('.mobile-plan-quantity-wrapper');
      if (mobileWrapper) {
        mobileWrapper.style.display = 'none';
      }
    }
  });
};